import api, {
  CouponsResponse,
  Customer,
  GetPromotionArgs,
} from '@sparemin/api-sdk';
import { normalize } from 'normalizr';

import { plansSchema, promotionsSchema } from './schemas';
import * as types from './types';

async function getPlans(
  args: types.GetPlansArgs,
): Promise<types.GetPlanResult> {
  const res = await api.getPlans(args);
  return normalize(res.data, [plansSchema]);
}

async function getMySubscription(): Promise<types.GetMySubscriptionResult> {
  return api.getMySubscription();
}

async function updateMySubscription(args: types.UpdateMySubscriptionArgs) {
  await api.updateMySubscription({
    ...args,
    // Defaulting to null means the subscription is not tied to an affiliate,
    // so the backend will remove any referral references from the user entry.
    rewardfulReferral: args?.rewardfulReferral || null,
  });
}

async function createPaymentMethod(
  args: types.CreatePaymentMethodArgs,
  userId: number,
) {
  await api.createPaymentMethod({ ...args, userId });
}

async function getCustomer(userId: number): Promise<Customer> {
  return api.getCustomer({ userId });
}

async function getMyCoupons(): Promise<CouponsResponse> {
  return api.getMyCoupons();
}

async function getMyReferral(): Promise<types.GetMyReferralResult> {
  return api.getMyReferralOwnerInfo();
}

async function getMyPodcastPromoCredits(): Promise<
  types.GetMyPodcastPromoCreditsResult
> {
  return api.getMyPodcastPromoCredits();
}

async function sendReferralInvitations(
  args: types.SendReferralInvitationsArgs,
) {
  await api.sendReferralEmailInvitations(args);
}

async function getExternalPortalLink(
  args: types.GetExternalPortalLinkArgs,
): Promise<types.GetExternalPortalLinkResult> {
  return api.createPaymentServicePortalLink(args);
}

async function getPromotion(
  args: GetPromotionArgs,
): Promise<types.GetPromotionResult> {
  const res = await api.getPromotion(args);
  return normalize(res, promotionsSchema);
}

async function acceptPromotion(
  args: types.AcceptPromotionArgs,
): Promise<types.AcceptPromotionResult> {
  return api.acceptPromotion(args);
}

export const handle = (
  method: any,
  args: any,
  _: string,
  userId: number,
): any => {
  switch (method) {
    case types.ServiceMethod.GET_PLANS:
      return getPlans(args);

    case types.ServiceMethod.GET_MY_SUBSCRIPTION:
      return getMySubscription();

    case types.ServiceMethod.UPDATE_MY_SUBSCRIPTION:
      return updateMySubscription(args);

    case types.ServiceMethod.CREATE_PAYMENT_METHOD:
      return createPaymentMethod(args, userId);

    case types.ServiceMethod.GET_CUSTOMER:
      return getCustomer(userId);

    case types.ServiceMethod.GET_MY_COUPONS:
      return getMyCoupons();

    case types.ServiceMethod.GET_MY_REFERRAL:
      return getMyReferral();

    case types.ServiceMethod.GET_MY_PODCAST_PROMO_CREDITS:
      return getMyPodcastPromoCredits();

    case types.ServiceMethod.SEND_REFERRAL_INVITATIONS:
      return sendReferralInvitations(args);

    case types.ServiceMethod.GET_EXTERNAL_PORTAL_LINK:
      return getExternalPortalLink(args);

    case types.ServiceMethod.GET_PROMOTION:
      return getPromotion(args);

    case types.ServiceMethod.ACCEPT_PROMOTION:
      return acceptPromotion(args);
  }

  return undefined;
};
